import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { SortRule } from '../../hooks/useMultiSort';
import { SortRuleRow, SortField } from './SortRuleRow';

interface SortPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  fields: SortField[];
  fullMatchesFirst: boolean;
  sorts: SortRule[];
  onFullMatchesFirstChange: (value: boolean) => void;
  onAddSort: () => void;
  onRemoveSort: (index: number) => void;
  onUpdateSort: (index: number, updates: Partial<SortRule>) => void;
  onReorderSorts: (fromIndex: number, toIndex: number) => void;
  onClear: () => void;
}

export const SortPopover: React.FC<SortPopoverProps> = ({
  isOpen,
  onClose,
  anchorEl,
  fields,
  fullMatchesFirst,
  sorts,
  onFullMatchesFirstChange,
  onAddSort,
  onRemoveSort,
  onUpdateSort,
  onReorderSorts,
  onClear
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Position popover near anchor
  useEffect(() => {
    if (isOpen && anchorEl && popoverRef.current) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popover = popoverRef.current;

      popover.style.top = `${anchorRect.bottom + 8}px`;
      popover.style.left = `${anchorRect.left}px`;

      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen, anchorEl]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorEl &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorEl]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Drag handlers
  const handleDragStart = (index: number) => {
    setDraggingIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggingIndex !== null && draggingIndex !== index) {
      setDropIndicatorIndex(index);
    }
  };

  const handleDrop = (index: number) => {
    if (draggingIndex !== null && draggingIndex !== index) {
      onReorderSorts(draggingIndex, index);
    }
    setDraggingIndex(null);
    setDropIndicatorIndex(null);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDropIndicatorIndex(null);
  };

  return (
    <>
      <style>{`
        @keyframes popoverIn {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        ref={popoverRef}
        role="dialog"
        aria-labelledby="sort-popover-title"
        className="fixed bg-white rounded-xl border border-gray-200"
        style={{
          width: '420px',
          maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.08), 0 20px 25px -5px rgba(0,0,0,0.05)',
          zIndex: 10000,
          animation: isVisible ? 'popoverIn 150ms ease-out forwards' : 'none',
          opacity: isVisible ? 1 : 0
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 id="sort-popover-title" className="text-[13px] font-semibold text-gray-900">
            Sort by
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Full matches first toggle */}
          <div className="flex items-center justify-between mb-4">
            <label
              htmlFor="full-matches-toggle"
              className="text-[12px] font-medium text-gray-600 cursor-pointer"
            >
              Full matches first
            </label>
            <button
              id="full-matches-toggle"
              role="switch"
              aria-checked={fullMatchesFirst}
              onClick={() => onFullMatchesFirstChange(!fullMatchesFirst)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                fullMatchesFirst ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
                  fullMatchesFirst ? 'translate-x-4' : 'translate-x-0.5'
                }`}
                style={{ marginTop: '2px' }}
              />
            </button>
          </div>

          {/* Sort rules */}
          {sorts.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
              No sort rules yet
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {sorts.map((sort, index) => (
                <SortRuleRow
                  key={index}
                  sort={sort}
                  index={index}
                  fields={fields}
                  isDragging={draggingIndex === index}
                  showDropIndicator={dropIndicatorIndex === index}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={() => handleDrop(index)}
                  onDragEnd={handleDragEnd}
                  onUpdate={(updates) => onUpdateSort(index, updates)}
                  onRemove={() => onRemoveSort(index)}
                />
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={onAddSort}
              className="text-[12px] font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              + Add sort
            </button>

            {sorts.length > 0 && (
              <button
                onClick={onClear}
                className="text-[12px] font-medium text-gray-400 hover:text-red-500 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
